'use client';

import Button from 'components/buttons/button';
import {
  POSTHOG_SIGNIN_EMAIL_EVENT,
  POSTHOG_SIGNIN_EMAIL_RESULT_EVENT,
  POSTHOG_SIGNIN_EVENT,
  SIGNIN_ENTRY_MAGIC_LINK,
  SIGNIN_ENTRY_MANUAL,
  useAnalytics,
} from 'hooks/useAnalytics';
import { useFallbackNext } from 'hooks/useFallbackNext';
import { useRouter } from 'next/navigation';
import { signIn, signOut, useSession } from 'next-auth/react';
import { useCallback, useEffect, useRef, useState } from 'react';

// import DevCodeRequest from './DevCodeRequest';

const CODE_LENGTH = 6;

type EmailLoginState = 'idle' | 'sending' | 'sent' | 'error';

/**
 * After a failed signIn attempt, check whether the backend stored a
 * structured error (e.g. UtilityBelt down) as opposed to a simple
 * "invalid / expired code" situation. Returns the rich error message
 * if one exists, otherwise null.
 */
async function checkLoginError(
  code: string
): Promise<{ message: string; sentryId: string | null } | null> {
  try {
    const res = await fetch(
      `/api/account/magic/email/login-error?code=${encodeURIComponent(code)}`
    );
    if (!res.ok) return null;
    const data = await res.json();
    if (data.error) {
      return {
        message: data.message || 'An unexpected error occurred.',
        sentryId: data.sentryId || null,
      };
    }
  } catch {
    // Network error checking for login error context — not critical
  }
  return null;
}

/** Format a rich login error, optionally including a Sentry reference. */
function formatLoginError(info: {
  message: string;
  sentryId: string | null;
}): string {
  if (info.sentryId) {
    return `${info.message} (Reference: ${info.sentryId})`;
  }
  return info.message;
}

export default function SignIn({
  initialCode = '',
  fallbackNext = '',
}: {
  initialCode?: string;
  fallbackNext?: string;
}) {
  // General state
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(!!initialCode);
  const [redirecting, setRedirecting] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const router = useRouter();
  const hasAttempted = useRef(false);
  const hasRedirected = useRef(false);
  const { data: session, status } = useSession();
  const { storeFallbackNext, getFallbackNext, clearFallbackNext } =
    useFallbackNext();
  const { trackEvent } = useAnalytics();

  // Email login state
  const [emailLoginState, setEmailLoginState] =
    useState<EmailLoginState>('idle');
  const [emailAddress, setEmailAddress] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailRetryAfter, setEmailRetryAfter] = useState(0);
  const emailInputRef = useRef<HTMLInputElement>(null);

  // Code input ref
  const codeInputRef = useRef<HTMLInputElement>(null);

  // Resend cooldown
  const [resendCooldown, setResendCooldown] = useState(0);

  // Derived
  const codeComplete = code.trim().length === CODE_LENGTH;

  // ---------------------------------------------------------------------------
  // localStorage fallbackNext persistence
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (fallbackNext) {
      storeFallbackNext(fallbackNext);
    }
  }, [fallbackNext, storeFallbackNext]);

  // ---------------------------------------------------------------------------
  // Redirect when session is established
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (
      session?.user?.id &&
      (!initialCode || (initialCode && !loading)) &&
      !hasRedirected.current
    ) {
      const storedFallback = getFallbackNext();
      const nextUrl =
        session.user.nextUrl?.trim() === '/'
          ? fallbackNext || storedFallback || '/chat'
          : session.user.nextUrl?.trim() ||
            fallbackNext ||
            storedFallback ||
            '/chat';

      hasRedirected.current = true;
      clearFallbackNext();
      setRedirecting(true);
      router.push(nextUrl);
    }
  }, [
    session?.user?.id,
    session?.user?.nextUrl,
    fallbackNext,
    router,
    initialCode,
    loading,
    getFallbackNext,
    clearFallbackNext,
  ]);

  // ---------------------------------------------------------------------------
  // Auto-login when initialCode is present (magic-link click)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const attemptAutoLogin = async () => {
      if (initialCode && !hasAttempted.current && status !== 'loading') {
        hasAttempted.current = true;
        setLoading(true);
        setError('');

        try {
          if (session?.user?.id) {
            await signOut({ redirect: false });
          }

          const result = await signIn('credentials', {
            redirect: false,
            code: initialCode,
          });

          if (result?.error) {
            const loginErr = await checkLoginError(initialCode);
            setLoading(false);
            setError(
              loginErr ? formatLoginError(loginErr) : 'Invalid or expired code'
            );
            trackEvent({
              eventName: POSTHOG_SIGNIN_EVENT,
              eventData: {
                success: false,
                entry: SIGNIN_ENTRY_MAGIC_LINK,
                reason: loginErr?.message || 'invalid_or_expired_code',
              },
            });
          } else {
            setLoading(false);
            setRedirecting(true);
            trackEvent({
              eventName: POSTHOG_SIGNIN_EVENT,
              eventData: { success: true, entry: SIGNIN_ENTRY_MAGIC_LINK },
            });
          }
        } catch (_err) {
          setLoading(false);
          setError('An error occurred during sign in');
        }
      }
    };

    attemptAutoLogin();
  }, [initialCode, status, session?.user?.id, trackEvent]);

  // Timeout to prevent infinite loading on auto-login
  useEffect(() => {
    if (loading && initialCode) {
      const timeout = setTimeout(() => {
        setLoading(false);
        setError('Sign in timed out. Please try again.');
      }, 10000);

      return () => clearTimeout(timeout);
    }
  }, [loading, initialCode]);

  // Fade in the "taking longer" fallback after 5 seconds
  useEffect(() => {
    if (loading && initialCode) {
      const timer = setTimeout(() => {
        setShowFallback(true);
      }, 5000);

      return () => clearTimeout(timer);
    } else {
      setShowFallback(false);
    }
  }, [loading, initialCode]);

  // Countdown timer for email rate-limit retry
  useEffect(() => {
    if (emailRetryAfter <= 0) return;

    const interval = setInterval(() => {
      setEmailRetryAfter((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [emailRetryAfter]);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown <= 0) return;

    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [resendCooldown]);

  // Autofocus email input on mount (only when no initialCode)
  useEffect(() => {
    if (!initialCode && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [initialCode]);

  // Focus code input when switching to 'sent' state
  useEffect(() => {
    if (emailLoginState === 'sent') {
      setTimeout(() => {
        codeInputRef.current?.focus();
      }, 50);
    }
  }, [emailLoginState]);

  // ---------------------------------------------------------------------------
  // Sign-in handler (credentials / code submission)
  // ---------------------------------------------------------------------------

  const handleVerify = useCallback(
    async (submittedCode?: string) => {
      const codeToUse = (submittedCode || code).trim().toUpperCase();
      if (codeToUse.length !== CODE_LENGTH) return;

      setLoading(true);
      setError('');

      try {
        if (session?.user?.id) {
          await signOut({ redirect: false });
        }

        const result = await signIn('credentials', {
          redirect: false,
          code: codeToUse,
        });

        let reason: string | undefined;
        if (result?.error) {
          const loginErr = await checkLoginError(codeToUse);
          setLoading(false);
          setError(
            loginErr ? formatLoginError(loginErr) : 'Invalid or expired code'
          );
          reason = loginErr?.message || 'invalid_or_expired_code';
        }
        // Never send the login code itself — it is a credential, and these
        // events are readable by anyone with analytics access.
        trackEvent({
          eventName: POSTHOG_SIGNIN_EVENT,
          eventData: {
            success: !result?.error,
            entry: SIGNIN_ENTRY_MANUAL,
            ...(reason ? { reason } : {}),
          },
        });
      } catch (_err) {
        setLoading(false);
        setError('An error occurred during sign in');
      }
    },
    [code, session?.user?.id, trackEvent]
  );

  // ---------------------------------------------------------------------------
  // Email send handler
  // ---------------------------------------------------------------------------

  const handleEmailSend = async () => {
    const trimmed = emailAddress.trim();
    if (!trimmed) return;

    setEmailLoginState('sending');
    setEmailError('');

    // Send the provider, not the address — the domain is what tells us whether
    // deliverability differs by mail provider, and it isn't personally
    // identifying the way the full address is.
    const emailDomain = trimmed.split('@')[1]?.toLowerCase() || 'unknown';

    trackEvent({
      eventName: POSTHOG_SIGNIN_EMAIL_EVENT,
      eventData: { email_domain: emailDomain },
    });

    try {
      const res = await fetch('/api/account/magic/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });

      if (res.ok) {
        setEmailLoginState('sent');
        setResendCooldown(30);
        // Clear any previous code / errors
        setCode('');
        setError('');
        trackEvent({
          eventName: POSTHOG_SIGNIN_EMAIL_RESULT_EVENT,
          eventData: { success: true, email_domain: emailDomain },
        });
      } else {
        const data = await res.json().catch(() => ({}));

        if (res.status === 429) {
          const retryAfter =
            data.retryAfter ||
            parseInt(res.headers.get('Retry-After') || '0', 10);
          setEmailRetryAfter(retryAfter);
          setEmailError(
            data.error || 'Too many requests. Please wait and try again.'
          );
        } else {
          setEmailError(
            data.error || 'Something went wrong. Please try again.'
          );
        }
        setEmailLoginState('error');
        trackEvent({
          eventName: POSTHOG_SIGNIN_EMAIL_RESULT_EVENT,
          eventData: {
            success: false,
            email_domain: emailDomain,
            status: res.status,
            reason: res.status === 429 ? 'rate_limited' : 'send_failed',
          },
        });
      }
    } catch (_err) {
      setEmailError(
        'Network error. Please check your connection and try again.'
      );
      setEmailLoginState('error');
      trackEvent({
        eventName: POSTHOG_SIGNIN_EMAIL_RESULT_EVENT,
        eventData: {
          success: false,
          email_domain: emailDomain,
          reason: 'network_error',
        },
      });
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    await handleEmailSend();
  };

  const handleEmailReset = () => {
    setEmailLoginState('idle');
    setEmailError('');
    setEmailRetryAfter(0);
    setCode('');
    setError('');
    // Keep emailAddress so the user can see / edit it
    setTimeout(() => emailInputRef.current?.focus(), 50);
  };

  // ---------------------------------------------------------------------------
  // Loading / redirecting screen
  // ---------------------------------------------------------------------------

  if ((loading && initialCode) || redirecting) {
    return (
      <div className="flex size-full flex-col items-center justify-center p-20">
        <div className="text-lg font-semibold">
          {redirecting ? 'Redirecting…' : 'Signing you in…'}
        </div>
        <div
          className={`mt-4 text-center text-sm text-gray-600 transition-opacity duration-500 ${
            showFallback ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <p className="mb-3">Taking longer than expected?</p>
          <Button
            theme="light"
            size="small"
            onClick={() => {
              setLoading(false);
              setError('Sign in timed out. Please try again.');
            }}
            className="ml-2 text-blue-600 underline"
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: email entry screen
  // ---------------------------------------------------------------------------

  const renderEmailScreen = () => (
    <div className="flex w-full flex-col gap-4">
      <div className="text-center">
        <h1 className="mb-1 text-2xl font-bold">Log In</h1>
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          Enter your email and we&rsquo;ll send you a&nbsp;code.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <div>
          <label htmlFor="email-login" className="sr-only">
            Email address
          </label>
          <input
            id="email-login"
            type="email"
            value={emailAddress}
            onChange={(e) => {
              setEmailAddress(e.target.value);
              if (emailError) setEmailError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && emailAddress.trim()) {
                e.preventDefault();
                handleEmailSend();
              }
            }}
            placeholder="you@example.com"
            className="w-full rounded-lg border border-zinc-300 bg-zinc-50 px-4 py-3 text-base focus:ring-2 focus:ring-blue-500 focus:outline-none dark:border-zinc-700 dark:bg-zinc-800"
            disabled={emailLoginState === 'sending'}
            ref={emailInputRef}
            autoComplete="email"
            autoCapitalize="off"
          />
        </div>

        {emailError && (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm dark:border-red-700 dark:bg-red-900/20">
            <div className="shrink-0 text-red-600 dark:text-red-400">
              <svg
                className="h-4 w-4"
                fill="currentColor"
                viewBox="0 0 20 20"
                role="img"
                aria-label="Error"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="text-red-800 dark:text-red-200">
              <p>{emailError}</p>
              {emailRetryAfter > 0 && (
                <p className="mt-1 text-xs">
                  You can try again in{' '}
                  <span className="font-semibold">
                    {Math.floor(emailRetryAfter / 60)}:
                    {String(emailRetryAfter % 60).padStart(2, '0')}
                  </span>
                </p>
              )}
            </div>
          </div>
        )}

        <Button
          type="button"
          theme="blue"
          onClick={handleEmailSend}
          disabled={
            !emailAddress.trim() ||
            emailLoginState === 'sending' ||
            emailRetryAfter > 0
          }
          className="w-full"
        >
          {emailLoginState === 'sending' ? 'Sending…' : 'Continue'}
        </Button>
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Render: verification code screen
  // ---------------------------------------------------------------------------

  const renderVerifyScreen = () => (
    <div className="flex w-full flex-col gap-5">
      <div className="text-center">
        <h1 className="mb-1 text-2xl font-bold">Check your email</h1>
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          We sent a 6-character code to{' '}
          <span className="font-semibold text-zinc-700 dark:text-zinc-200">
            {emailAddress}
          </span>
        </p>
      </div>

      <div>
        <label
          htmlFor="verification-code"
          className="mb-2 block text-center text-xs font-semibold tracking-widest text-zinc-500 uppercase dark:text-zinc-400"
        >
          Verification code
        </label>
        <input
          id="verification-code"
          ref={codeInputRef}
          type="text"
          autoComplete="one-time-code"
          autoCapitalize="characters"
          spellCheck={false}
          autoCorrect="off"
          maxLength={CODE_LENGTH}
          value={code}
          onChange={(e) => {
            const val = e.target.value
              .replace(/[^A-Za-z0-9]/g, '')
              .toUpperCase()
              .slice(0, CODE_LENGTH);
            setCode(val);
            if (error) setError('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && codeComplete) {
              e.preventDefault();
              handleVerify();
            }
          }}
          placeholder="ABC123"
          className={`w-full rounded-lg border px-4 py-3 text-center font-mono text-2xl font-bold tracking-[0.3em] uppercase transition-colors focus:ring-2 focus:ring-blue-500 focus:outline-none ${
            error
              ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20'
              : 'border-zinc-300 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800'
          }`}
          disabled={loading}
        />
      </div>

      {error && (
        <div className="text-center text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      <Button
        type="button"
        theme="blue"
        onClick={() => handleVerify()}
        disabled={!codeComplete || loading}
        className="w-full"
      >
        {loading ? 'Verifying…' : 'Verify'}
      </Button>

      <div className="flex flex-col items-center gap-2 text-sm">
        <p className="text-zinc-500 dark:text-zinc-400">
          Didn&rsquo;t get it?{' '}
          {resendCooldown > 0 ? (
            <span className="text-zinc-400 dark:text-zinc-500">
              Resend in {resendCooldown}s
            </span>
          ) : (
            <button
              type="button"
              onClick={handleResend}
              className="font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              Resend code
            </button>
          )}
        </p>
        <button
          type="button"
          onClick={handleEmailReset}
          className="text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
        >
          &larr; Use a different email
        </button>
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  return (
    <div className="mt-12 flex size-full flex-col items-center justify-center p-6">
      {/* Dev-only login code helper */}
      {/*{process.env.IS_PROD === 'false' && <DevCodeRequest />}*/}

      <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-md dark:bg-zinc-900">
        {emailLoginState === 'sent'
          ? renderVerifyScreen()
          : renderEmailScreen()}
      </div>
    </div>
  );
}
